<script setup lang="ts">
/**
 * Ticket edit dialog — the shell that ties header, tabs, and content together.
 *
 * Responsibilities:
 * - Dialog visibility lifecycle
 * - Ticket loading via useTicketEdit composable
 * - Tab routing
 * - Wiring child components to ticket state
 *
 * NOT responsible for: entity state (TicketEditEntities), update logic (useTicketEdit),
 * sidebar layout (TicketEditProperties), or header display (TicketEditHeader).
 */
import 'src/assets/ticket-edit-shared.css';
import { ref, watch, computed, nextTick, onUnmounted, toRef } from 'vue';
import Dialog from 'primevue/dialog';
import type { FetchTicketResponse } from 'src/api/models';
import type { AssigneeData } from 'src/services/assignee-service';
import { useTicketEdit } from 'src/composables/useTicketEdit';
import TicketEditHeader from './TicketEditHeader.vue';
import TicketEditEntities from './TicketEditEntities.vue';
import TicketEditProperties from './TicketEditProperties.vue';
import TicketEditSectionHeader from './TicketEditSectionHeader.vue';

import TicketEditComments from './TicketEditComments.vue';
import TicketEditAssignments from './TicketEditAssignments.vue';
import TicketEditArticles from './TicketEditArticles.vue';
import TicketEditMedia from './TicketEditMedia.vue';
import TicketEditContracts from './TicketEditContracts.vue';

const props = defineProps<{ ticketId: string }>();
const modelValue = defineModel<boolean>();

const emit = defineEmits<{
  'update:ticket': [ticket: FetchTicketResponse];
}>();

const activeTab = ref('details');
const entitiesRef = ref<InstanceType<typeof TicketEditEntities>>();


const ticketIdRef = toRef(props, 'ticketId');

const {
  ticket,
  isLoading,
  loadTicket,
  reset,
  getScheduledDate,
  getExpectedDuration,
  updateTitle,
  updateDescription,
  updatePriority,
  updateStatus,
  updateStatusDetail,
  updateAssignee,
  updateScheduledDate,
  updateExpectedDuration,
} = useTicketEdit({
  ticketId: ticketIdRef,
  async onTicketLoaded(t) {
    await nextTick();
    entitiesRef.value?.initializeFromTicket(t);
  },
});


const scheduledDate = computed(() => getScheduledDate());
const expectedDuration = computed(() => getExpectedDuration());


const tabs = computed(() => [
  { id: 'details', label: 'Details', icon: 'pi-file-edit' },
  { id: 'comments', label: 'Comments', icon: 'pi-comments' },
  { id: 'assignments', label: 'Assignments', icon: 'pi-users' },
  { id: 'articles', label: 'Articles', icon: 'pi-box' },
  { id: 'media', label: 'Media', icon: 'pi-images' },
  { id: 'contracts', label: 'Contracts', icon: 'pi-file', disabled: !ticket.value?.unit },
]);


watch(modelValue, async (isOpen) => {
  if (isOpen) {
    activeTab.value = 'details';
    await loadTicket();
  } else {
    if (ticket.value) emit('update:ticket', ticket.value);
    entitiesRef.value?.reset();
    reset();
  }
}, { immediate: true });

function handleClose(): void {
  modelValue.value = false;
}

onUnmounted(() => {
  entitiesRef.value?.reset();
  reset();
});
</script>

<template>
  <Dialog
    v-model:visible="modelValue"
    :style="{ width: '85vw', maxWidth: '1200px' }"
    :breakpoints="{ '1766px': '90vw', '960px': '100vw' }"
    :content-style="{ height: '88vh' }"
    modal
    closable
    :pt="{
      header: { class: '!p-0 !hidden' },
      content: { class: '!p-0 !flex !flex-col !overflow-hidden' },
    }"
  >
    <template v-if="ticket">
      <TicketEditHeader
        :ticket-number="ticket.number"
        :title="ticket.title"
        :ticket-id="ticketId"
        @close="handleClose"
      />

      <div class="border-b border-surface-200 dark:border-surface-700 shrink-0">
        <nav class="flex gap-0.5 -mb-px">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            class="tab-btn"
            :class="[
              activeTab === tab.id ? 'tab-active' : 'tab-inactive',
              { 'opacity-40 pointer-events-none': tab.disabled },
            ]"
            @click="activeTab = tab.id"
          >
            <i :class="['pi', tab.icon]" style="font-size: 13px" />
            {{ tab.label }}
            <span v-if="activeTab === tab.id" class="tab-indicator" />
          </button>
        </nav>
      </div>

      <div class="flex-1 overflow-y-auto">
        <!-- Details tab -->
        <div v-show="activeTab === 'details'" class="flex min-h-0">
          <!-- Left: main content -->
          <div class="flex-1 p-3">
            <section>
              <TicketEditEntities
                ref="entitiesRef"
                :ticket-id="ticketId"
              />
            </section>
          </div>

          <!-- Right: properties sidebar -->
          <aside class="w-[300px] shrink-0 border-l border-surface-200 dark:border-surface-700 bg-surface-50/50 dark:bg-surface-800/20">
            <TicketEditProperties
              :title="ticket.title"
              @update:title="updateTitle"
              :description="ticket.description"
              @update:description="updateDescription"
              :scheduled-date="scheduledDate"
              @update:scheduled-date="updateScheduledDate"
              :expected-duration="expectedDuration"
              @update:expected-duration="updateExpectedDuration"
              :priority="ticket.priority"
              @update:priority="updatePriority"
              :status="ticket.status"
              :status-detail="ticket.status_detail"
              @update:status="updateStatus"
              @update:status-detail="updateStatusDetail"
              :assignee="(ticket.assignee as AssigneeData)"
              @update:assignee="updateAssignee"
              :created-at="ticket.created_at"
              show-status
            />
          </aside>
        </div>

        <!-- Comments tab -->
        <TicketEditComments
          v-if="activeTab === 'comments'"
          :ticket-id="ticketId"
        />

        <!-- Assignments tab -->
        <TicketEditAssignments
          v-if="activeTab === 'assignments'"
          :ticket-id="ticketId"
          :ticket="ticket"
          @refresh-ticket="loadTicket"
        />

        <!-- Articles tab -->
        <TicketEditArticles
          v-if="activeTab === 'articles'"
          :ticket="ticket"
          v-model:articles="(ticket.articles as any)"
          @refresh-ticket="loadTicket"
        />

        <!-- Media tab -->
        <TicketEditMedia
          v-show="activeTab === 'media'"
          :ticket-id="ticketId"
          :files="ticket.files ?? []"
        />

        <!-- Contracts tab -->
        <TicketEditContracts
          v-if="activeTab === 'contracts'"
          :ticket="ticket"
          :ticket-id="ticketId"
          @refresh-ticket="loadTicket"
        />
      </div>
    </template>

    <!-- Loading state -->
    <div v-else-if="isLoading" class="flex items-center justify-center h-48">
      <i class="pi pi-spinner pi-spin text-surface-400" style="font-size: 24px" />
    </div>
  </Dialog>
</template>

<style scoped>
.tab-btn {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.625rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  transition: color 0.15s;
}
.tab-active {
  color: var(--p-primary-600);
}
:root[class*='app-dark'] .tab-active {
  color: var(--p-primary-400);
}
.tab-inactive {
  color: var(--p-surface-500);
}
.tab-inactive:hover {
  color: var(--p-surface-700);
}
:root[class*='app-dark'] .tab-inactive:hover {
  color: var(--p-surface-300);
}
.tab-indicator {
  position: absolute;
  bottom: 0;
  left: 0.5rem;
  right: 0.5rem;
  height: 2px;
  border-radius: 9999px;
  background: var(--p-primary-500);
}
</style>
